import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { searchPatients } from '@/pages/main/patient/services';
import { PatientFormData } from '@/pages/main/patient/types/patientTypes';

/** Page size for patient search (must be ≤ backend `per_page` max, see PatientSearchRequest). */
export const PATIENT_SEARCH_PAGE_SIZE = 50;
export const PATIENT_SEARCH_INFINITE_STALE_TIME = 0;
export const PATIENT_SEARCH_INFINITE_GC_TIME = 0;

export interface PatientSearchInfiniteFilters {
  keyword: string;
  includeExcluded: boolean;
  sortField: string;
  sortOrder: 'asc' | 'desc';
  /** 施術入力検索用: official=1 のみ */
  officialOnly?: boolean;
}

interface UsePatientSearchOptions {
  siteType: string;
  clinicNumber: string;
  searchParams: {
    keyword: string;
    includeExcluded: boolean;
    page: number;
    sortField: string;
    sortOrder: 'asc' | 'desc';
  } | null;
}

interface UsePatientSearchReturn {
  patients: PatientFormData[];
  isLoading: boolean;
  isError: boolean;
  error: any;
  pagination: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    from: number | null;
    to: number | null;
  };
}

export const usePatientSearch = ({
  siteType,
  clinicNumber,
  searchParams,
}: UsePatientSearchOptions): UsePatientSearchReturn => {
  // Keep previous data while loading to prevent layout shift
  const [previousData, setPreviousData] = useState<PatientFormData[]>([]);
  const [previousPagination, setPreviousPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: PATIENT_SEARCH_PAGE_SIZE,
    total: 0,
    from: null as number | null,
    to: null as number | null,
  });

  const query = useQuery({
    queryKey: ['patientSearch', searchParams],
    queryFn: async () => {
      if (!searchParams) return null;

      const result = await searchPatients(
        siteType,
        clinicNumber,
        searchParams.keyword,
        searchParams.includeExcluded,
        searchParams.page,
        PATIENT_SEARCH_PAGE_SIZE,
        searchParams.sortField,
        searchParams.sortOrder
      );

      return result;
    },
    enabled: !!searchParams,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // Update previous data only when new data successfully loads
  useEffect(() => {
    if (query.data?.data) {
      setPreviousData(query.data.data);
      setPreviousPagination(query.data.pagination);
    }
  }, [query.data?.data, query.data?.pagination]);

  // Use previous data while loading to prevent layout shift
  const displayPatients =
    query.isLoading && previousData.length > 0 ? previousData : query.data?.data || [];
  const displayPagination =
    query.isLoading && previousData.length > 0
      ? previousPagination
      : query.data?.pagination || {
          current_page: 1,
          last_page: 1,
          per_page: PATIENT_SEARCH_PAGE_SIZE,
          total: 0,
          from: null,
          to: null,
        };

  return {
    patients: displayPatients,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    pagination: displayPagination,
  };
};

/**
 * Infinite scroll / paginated patient search (clinic scope).
 * Same API as {@link usePatientSearch} but accumulates pages; use with scroll-to-load UI.
 */
export function useInfinitePatientSearch(
  siteType: string,
  clinicNumber: string,
  filters: PatientSearchInfiniteFilters | null,
  options?: { enabled?: boolean }
) {
  const enabled = options?.enabled ?? true;

  return useInfiniteQuery({
    queryKey: ['patientSearch', 'infinite', siteType, clinicNumber, filters],
    queryFn: async ({ pageParam = 1 }) => {
      if (!filters) {
        throw new Error('Patient search filters are required');
      }
      return searchPatients(
        siteType,
        clinicNumber,
        filters.keyword,
        filters.includeExcluded,
        pageParam as number,
        PATIENT_SEARCH_PAGE_SIZE,
        filters.sortField,
        filters.sortOrder,
        undefined,
        undefined,
        filters.officialOnly
      );
    },
    initialPageParam: 1,
    getNextPageParam: lastPage => {
      const p = lastPage.pagination;
      if (p.current_page < p.last_page) {
        return p.current_page + 1;
      }
      return undefined;
    },
    enabled: enabled && !!siteType && !!clinicNumber && filters !== null,
    staleTime: PATIENT_SEARCH_INFINITE_STALE_TIME,
    gcTime: PATIENT_SEARCH_INFINITE_GC_TIME,
  });
}
